import { TranslateResult } from 'vue-i18n';
import { ToastButton } from '@ionic/core/dist/types/components/toast/toast-interface';

import { TitleListName } from '@/interfaces/responses/title-list';

import { getVm } from '@/helpers/vm-helper';
import { ObjectType } from '@/@types/graphql-types';

let tCtr: any = null;

export interface ToastCallback {
	message: string;
	callback: Function;
}

export enum ToastIconSide {
	START = 'start',
	END = 'end',
}

export type ToastColors =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'success'
	| 'warning'
	| 'danger'
	| 'light'
	| 'medium'
	| 'dark';

export type ToastPosition = 'bottom' | 'middle' | 'top';
export type ToastAction = 'add' | 'remove';

const TOAST_DEFAULT_MESSAGE_TIMEOUT = 1500;
const LIST_TRANSLATABLES = () => ({
	add: {
		[TitleListName.WATCHLIST]: getVm().$t('WEBAPP_WATCHLIST_TOAST_ADDED'),
		[TitleListName.SEENLIST]: getVm().$t('WEBAPP_SEENLIST_TOAST_SEEN'),
		[TitleListName.LIKELIST]: getVm().$t('WEBAPP_LIKELIST_TOAST_ADDED'),
		[TitleListName.DISLIKELIST]: getVm().$t('WEBAPP_DISLIKELIST_TOAST_ADDED'),
	},
	remove: {
		[TitleListName.WATCHLIST]: getVm().$t('WEBAPP_WATCHLIST_TOAST_REMOVED'),
		[TitleListName.SEENLIST]: getVm().$t('WEBAPP_SEENLIST_TOAST_UNSEEN'),
		[TitleListName.LIKELIST]: getVm().$t('WEBAPP_LIKELIST_TOAST_REMOVED'),
		[TitleListName.DISLIKELIST]: getVm().$t('WEBAPP_DISLIKELIST_TOAST_REMOVED'),
	},
});

const ToastHelper = {
	translatable: null as Record<ToastAction, any> | null,

	async init() {
		const { addIcons, checkmarkCircleOutline, arrowUndo } = await import('@/ionic.loader');

		// manually need to add ion-icon
		addIcons({
			undo: arrowUndo,
			'checkmark-circle-outline': checkmarkCircleOutline,
		});

		if (!tCtr) {
			const { toastController } = await import('@/ionic.loader');
			tCtr = toastController;
		}
	},

	async add({
		message,
		duration = TOAST_DEFAULT_MESSAGE_TIMEOUT,
		buttons = [],
		position = 'bottom',
		color = 'tertiary',
		cssClass = 'jw-toast',
		showCloseButton = true,
	}: {
		message: string;
		duration?: number;
		buttons?: ToastButton[];
		position?: ToastPosition;
		color?: ToastColors;
		cssClass?: string;
		showCloseButton?: boolean;
	}) {
		if (showCloseButton) {
			const closeButtonText = getVm().$t('WEBAPP_CLOSE') as string;
			const closeButton: ToastButton = {
				text: closeButtonText,
				role: 'cancel',
				side: ToastIconSide.END,
			};
			buttons = [...buttons, closeButton];
		}

		await this.init();

		const toast = await tCtr.create({
			message,
			duration,
			position,
			color,
			cssClass,
			buttons,
		});

		return await toast.present();
	},

	success({
		message,
		action,
		duration = TOAST_DEFAULT_MESSAGE_TIMEOUT,
		closeButton = true,
	}: {
		message: string;
		action?: ToastButton;
		duration?: number;
		closeButton?: boolean;
	}) {
		const icon: ToastButton = {
			icon: 'checkmark-circle-outline',
			side: ToastIconSide.START,
		};

		const buttons: ToastButton[] = [icon];
		if (action) {
			buttons.push(action);
			closeButton = false;
		}
		this.add({ message, duration, buttons, showCloseButton: closeButton });
	},

	error({
		message,
		action,
		duration = TOAST_DEFAULT_MESSAGE_TIMEOUT,
		closeButton = true,
	}: {
		message: string;
		action?: ToastButton;
		duration?: number;
		closeButton?: boolean;
	}) {
		const icon: ToastButton = {
			icon: 'close',
			side: ToastIconSide.START,
		};

		const buttons: ToastButton[] = [icon];
		if (action) {
			buttons.push(action);
			closeButton = false;
		}

		this.add({ message, duration, buttons, color: 'danger', showCloseButton: closeButton });
	},

	successWithUndo({
		message,
		undoCallback,
		duration,
	}: {
		message: string;
		undoCallback: ToastCallback;
		duration?: number;
	}) {
		const { message: undoMessage, callback } = undoCallback;
		// Undo Action callback wrapper
		const undoActionCallback = (message: TranslateResult) => async () => {
			const icon: ToastButton = {
				icon: 'undo',
				side: ToastIconSide.START,
			};

			callback();

			this.add({ message: String(message), duration, buttons: [icon] });
		};

		// Undo Action for Toast
		const trUndoButtonText = getVm().$t('WEBAPP_UNDO_BUTTON') as string;
		const action: ToastButton = this.createToastAction(trUndoButtonText, undoActionCallback(undoMessage));
		this.success({ message, action, duration, closeButton: false });
	},

	createToastAction(text: string, callback: Function): ToastButton {
		return {
			text,
			handler: () => {
				try {
					callback();
				} catch (err) {
					// error message
					console.error('[createToastAction] callback err: ', err);
				}
			},
		};
	},

	getListTranslatable(listAction: ToastAction, listType: TitleListName) {
		if (!this.translatable) {
			this.translatable = LIST_TRANSLATABLES();
		}
		return (this.translatable[listAction] as any)[listType] as string;
	},
};

export function getSeenToastMessage(
	isTitleInSeenlist: boolean,
	payload: {
		titleObjectType: ObjectType;
		titleName: string;
		showSeason?: number;
		showEpisode?: number;
	}
) {
	const trUnseenMessage = getVm().$t('WEBAPP_SEENLIST_TOAST_UNSEEN');
	const trSeenMessage = getVm().$t('WEBAPP_SEENLIST_TOAST_SEEN');
	const { titleName, showEpisode, showSeason } = payload;
	let messageTitle = titleName;

	if (showSeason) {
		messageTitle += ` S${showSeason}`;
	}

	if (showEpisode) {
		messageTitle += ` E${showSeason}`;
	}

	return !isTitleInSeenlist ? `${messageTitle} ${trSeenMessage}` : `${messageTitle} ${trUnseenMessage}`;
}

export function addTitleToListToast(titleListName: TitleListName, title: string, undoCallback?: Function) {
	// exclude more lists for now
	if (titleListName !== TitleListName.MORELIST) {
		const trAddMessage = ToastHelper.getListTranslatable('add', titleListName);
		const trRemoveMessage = ToastHelper.getListTranslatable('remove', titleListName);

		const undoAction = {
			message: `${title} ${trRemoveMessage}`,
			callback: () => undoCallback && undoCallback(),
		};
		ToastHelper.successWithUndo({ message: `${title} ${trAddMessage}`, undoCallback: undoAction });
	}
}

export function removeTitleFromListToast(titleListName: TitleListName, title: string) {
	// exclude more lists for now
	if (titleListName !== TitleListName.MORELIST) {
		const trRemoveMessage = ToastHelper.getListTranslatable('remove', titleListName);
		ToastHelper.success({ message: `${title} ${trRemoveMessage}` });
	}
}

export default ToastHelper;
